import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import axios from 'axios';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getEntity } from './bank.reducer';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { IRebateSchedule, ScheduleSection, browserTimeZone, emptySchedule, isScheduleValid, periodLabel, scheduleFromBank } from './rebate/schedule-section';
import { EmailSection, isEmailListValid } from './rebate/email-section';
import { LetterSection, isLetterSettingValid } from './rebate/letter-section';
import {
  IRebateReportRow,
  IRebateReportSetting,
  REPORT_COLUMNS,
  ReportSection,
  columnValue,
  emptyReportSetting,
  isReportSettingValid,
  reportSettingFromBank,
} from './rebate/report-section';
import {
  IRebateDocumentAttachSetting,
  DocumentSection,
  emptyDocumentAttachSetting,
  isDocumentAttachSettingValid,
  documentAttachSettingFromBank,
} from './rebate/document-section';
import { getEntitiesByRebateSettingId as getRebateDocumentEmails } from 'app/entities/rebate-document-email/rebate-document-email.reducer';
import { PreviewSection } from './rebate/preview-section';
import { SettingPicker } from './rebate/setting-picker';
import { PendingAmountChecker } from './rebate/pending-amount-checker';

type RebateCommodityType = 'Local' | 'International';

const COMMODITY_TYPES: RebateCommodityType[] = ['International', 'Local'];

// Sections of the rebate setup. They look like steps, but any section can be opened directly.
// 'preview' is the final step: nothing is persisted to the DB until its Save button is clicked.
const SECTIONS = [
  { key: 'schedule', optional: false },
  { key: 'email', optional: false },
  { key: 'letter', optional: true },
  { key: 'reports', optional: true },
  { key: 'document', optional: true },
  { key: 'preview', optional: false },
];

const ACTIVE_COLOR = '#2476f0';
const INACTIVE_COLOR = '#dcdcdc';

const SectionIcon = ({ state }: { state: 'done' | 'active' | 'pending' }) => {
  if (state === 'done') {
    return <CheckCircleIcon sx={{ fontSize: 20, color: ACTIVE_COLOR }} />;
  }
  return (
    <Box
      component="span"
      sx={{
        width: 18,
        height: 18,
        margin: '1px',
        borderRadius: '50%',
        flexShrink: 0,
        boxSizing: 'border-box',
        ...(state === 'active'
          ? { border: `2px solid ${ACTIVE_COLOR}`, background: `linear-gradient(90deg, ${ACTIVE_COLOR} 50%, transparent 50%)` }
          : { background: INACTIVE_COLOR }),
      }}
    />
  );
};

export const BankRebate = (props: RouteComponentProps<{ id: string }>) => {
  const dispatch = useAppDispatch();
  const bank = useAppSelector(state => state.bank.entity);
  const settings = useAppSelector(state => state.rebateSetting.entities);
  const savedEmails = useAppSelector(state => state.rebateDocumentEmail.entities);
  const currentLocale = useAppSelector(state => state.locale.currentLocale);
  const localDay = dayjs().locale(currentLocale || 'en');

  const [tabIndex, setTabIndex] = useState(0);
  const [sectionByType, setSectionByType] = useState<Record<RebateCommodityType, number>>({ Local: 0, International: 0 });
  const [selectedSettingIdByType, setSelectedSettingIdByType] = useState<Record<RebateCommodityType, number | string>>({
    Local: '',
    International: '',
  });

  const commodityType = COMMODITY_TYPES[tabIndex];
  const activeSection = sectionByType[commodityType];
  const selectedSettingId = selectedSettingIdByType[commodityType];
  const currentSetting = settings.find(s => s.id === selectedSettingId);

  const setActiveSection = (index: number) => {
    setSectionByType({ ...sectionByType, [commodityType]: index });
    setShowErrors(false);
  };

  const [schedule, setSchedule] = useState<IRebateSchedule>(emptySchedule);
  const [emails, setEmails] = useState<string[]>([]);
  const [letterEnabled, setLetterEnabled] = useState(true);
  const [letterCategory, setLetterCategory] = useState('');
  const [letterLanguage, setLetterLanguage] = useState('EN');
  const [reportSetting, setReportSetting] = useState<IRebateReportSetting>(emptyReportSetting);
  const [documentSetting, setDocumentSetting] = useState<IRebateDocumentAttachSetting>(emptyDocumentAttachSetting);
  const [showErrors, setShowErrors] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [overpaymentWarning, setOverpaymentWarning] = useState(false);

  useEffect(() => {
    dispatch(getEntity(props.match.params.id));
  }, [props.match.params.id]);

  // Loads the currently selected setting's own data (AC: "every section loads and saves only that
  // setting's data"). Re-populates from the last-SAVED copy whenever the selection changes — any
  // unsaved local edits for a setting that's switched away from are discarded, matching "I select
  // one, every section loads" literally.
  useEffect(() => {
    if (!currentSetting) {
      setSchedule(emptySchedule);
      setLetterEnabled(true);
      setLetterCategory('');
      setLetterLanguage('EN');
      setReportSetting(emptyReportSetting);
      setDocumentSetting(emptyDocumentAttachSetting);
      return;
    }
    setSchedule(scheduleFromBank(currentSetting.rebateSchedule, undefined, undefined));
    // Settings saved before ALSHMOKH-59 have no rebateLetterEnabled value yet; only an explicit
    // false (set via this wizard's toggle) turns the letter off (AC: "banks that already have a
    // letter set up keep it turned on").
    setLetterEnabled(currentSetting.rebateLetterEnabled !== false);
    setLetterCategory(currentSetting.rebateLetterCategory || '');
    setLetterLanguage(currentSetting.rebateLetterLanguage || 'EN');
    setReportSetting(reportSettingFromBank(currentSetting.rebateReportSettings));
    setDocumentSetting(documentAttachSettingFromBank(currentSetting.rebateDocumentAttachSettings));
  }, [
    currentSetting?.id,
    currentSetting?.rebateSchedule,
    currentSetting?.rebateLetterEnabled,
    currentSetting?.rebateLetterCategory,
    currentSetting?.rebateLetterLanguage,
    currentSetting?.rebateReportSettings,
    currentSetting?.rebateDocumentAttachSettings,
  ]);

  // Fetches and applies a setting's recipient list, guarding against out-of-order responses: Redux
  // only knows "last fulfilled wins", not "last dispatched wins", so a slow mount-time fetch that
  // resolves after a later one (e.g. Save's post-write refresh) would otherwise silently overwrite
  // freshly-saved emails with stale pre-save data. Tracking the latest request's token here ensures
  // only the most recently issued fetch is ever applied to local state.
  const emailFetchToken = useRef(0);

  const refreshEmails = async (settingId: number | string) => {
    const token = ++emailFetchToken.current;
    const action = await dispatch(getRebateDocumentEmails(settingId));
    if (token === emailFetchToken.current && getRebateDocumentEmails.fulfilled.match(action)) {
      setEmails(action.payload.data.map(e => e.email));
    }
  };

  useEffect(() => {
    if (selectedSettingId) {
      refreshEmails(selectedSettingId);
    } else {
      emailFetchToken.current++;
      setEmails([]);
    }
  }, [selectedSettingId]);

  const sectionKey = SECTIONS[activeSection].key;
  const isLastSection = activeSection === SECTIONS.length - 1;
  const hasSelectedSetting = Boolean(selectedSettingId);

  // Each real section's own validity. 'preview' has nothing to validate itself.
  const sectionValidity: Record<string, boolean> = {
    schedule: isScheduleValid(schedule),
    email: isEmailListValid(emails),
    letter: isLetterSettingValid(letterEnabled, letterCategory),
    reports: isReportSettingValid(reportSetting),
    document: isDocumentAttachSettingValid(documentSetting),
  };

  const isCurrentSectionValid = sectionKey === 'preview' || sectionValidity[sectionKey];

  // Fetches the over-payment warning (ALSHMOKH-56) whenever the Preview step for a saved setting
  // is opened — reuses the same preview endpoint the print pages use; docType is fixed to
  // 'invoice' here since only the overpaymentWarning field (bank + commodityType scoped, not
  // docType-specific) is needed.
  useEffect(() => {
    if (sectionKey === 'preview' && selectedSettingId) {
      axios
        .get(`api/rebate-documents/preview?rebateSettingId=${selectedSettingId}&docType=invoice`)
        .then(({ data }) => setOverpaymentWarning(Boolean(data?.overpaymentWarning)))
        .catch(() => setOverpaymentWarning(false));
    }
  }, [sectionKey, selectedSettingId]);

  const handleBack = () => {
    if (activeSection > 0) {
      setActiveSection(activeSection - 1);
    }
  };

  // Moves to the next section. Nothing is persisted here: every section's data stays in local
  // state until the user reaches the preview step and clicks Save.
  const handleNext = () => {
    if (!isCurrentSectionValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    if (activeSection < SECTIONS.length - 1) {
      setActiveSection(activeSection + 1);
    }
  };

  // Final save from the preview step: persists every section's data for the currently selected
  // setting in one go. Since any section can be opened directly, re-validate all of them (sections
  // can be reached without ever triggering handleNext's per-section check).
  const handleSave = async () => {
    if (!selectedSettingId) {
      return;
    }
    const firstInvalidIndex = SECTIONS.findIndex(section => section.key !== 'preview' && !sectionValidity[section.key]);
    if (firstInvalidIndex !== -1) {
      setActiveSection(firstInvalidIndex);
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    const configuration = {
      rebateSchedule: JSON.stringify({ ...schedule, timeZone: browserTimeZone() }),
      rebateLetterEnabled: letterEnabled,
      rebateLetterCategory: letterCategory,
      rebateLetterLanguage: letterLanguage,
      rebateReportSettings: JSON.stringify(reportSetting),
      rebateDocumentAttachSettings: JSON.stringify(documentSetting),
    };

    const existingEmails = savedEmails;
    const emailsToAdd = emails.filter(email => !existingEmails.some(e => e.email === email));
    const emailsToRemove = existingEmails.filter(e => !emails.includes(e.email));

    // Calls go straight through axios rather than the usual entity thunks: those thunks' fulfilled
    // actions each carry their own success-toast header, and this save can fire one configuration
    // update plus several email creates/deletes at once - dispatching them would show a toast per
    // request instead of the single confirmation below.
    try {
      await Promise.all([
        axios.put(`api/rebate-settings/${selectedSettingId}/configuration`, configuration),
        ...emailsToAdd.map(email => axios.post('api/rebateDocumentEmails', { rebateSettingId: selectedSettingId, email, commodityType })),
        ...emailsToRemove.map(e => axios.delete(`api/rebateDocumentEmails/${e.id}`)),
      ]);
    } catch (error) {
      toast.error(translate('comexApp.bank.rebate.preview.saveError', { bankName: bank?.name ?? '' }));
      return;
    }

    await refreshEmails(selectedSettingId);

    toast.success(translate('comexApp.bank.rebate.preview.saveSuccess', { bankName: bank?.name ?? '' }));
  };

  // Generates and downloads the Excel report for the currently selected (not necessarily saved)
  // schedule period and report columns — mirrors the client-side exceljs pattern reports.tsx and
  // fee-reports.tsx already use for their exports.
  const handleGenerateReport = async () => {
    if (!selectedSettingId) {
      return;
    }
    setGeneratingReport(true);
    try {
      const { data } = await axios.post<IRebateReportRow[]>('api/rebate-documents/report', {
        rebateSettingId: selectedSettingId,
        schedule: JSON.stringify({ ...schedule, timeZone: browserTimeZone() }),
      });

      if (data.length === 0) {
        toast.info(translate('comexApp.bank.rebate.report.noTransactions'));
        return;
      }

      const selectedColumns = REPORT_COLUMNS.filter(column => reportSetting.columns.includes(column));
      if (data.some(row => row.pendingNote)) {
        selectedColumns.push('pendingNote');
      }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Rebate Report');
      ws.columns = selectedColumns.map(() => ({ width: 22 }));

      const headerRow = ws.addRow(selectedColumns.map(column => translate(`comexApp.bank.rebate.report.columns.${column}`, { bankName: bank?.name ?? '' })));
      headerRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2476F0' } };
      });

      data.forEach(row => {
        ws.addRow(selectedColumns.map(column => columnValue(row, column)));
      });

      const buffer = await wb.xlsx.writeBuffer();
      const label = periodLabel(localDay, schedule);
      saveAs(new Blob([buffer]), `Rebate Report${label ? ` - ${label}` : ''}.xlsx`);

      toast.success(translate('comexApp.bank.rebate.report.generateSuccess'));
    } catch (error) {
      toast.error(translate('comexApp.bank.rebate.report.generateError'));
    } finally {
      setGeneratingReport(false);
    }
  };

  const sectionLabel =(section: typeof SECTIONS[number]) =>
    `${translate(`comexApp.bank.rebate.sections.${section.key}`)}${section.optional ? ` ${translate('comexApp.bank.rebate.optional')}` : ''}`;

  return (
    <Container>
      <div className="mt-4 mb-4">
        <Button variant="text" startIcon={<ArrowBackIcon />} onClick={() => props.history.push('/rebate')} sx={{ mb: 2, textTransform: 'none' }}>
          {translate('entity.action.back')}
        </Button>
        <Typography variant="h4" component="div" data-cy="bankRebateHeading" className="mb-4">
          {translate('comexApp.bank.home.rebate')}{bank?.name ? ` - ${bank.name}` : ''}
        </Typography>

        <Paper className="paper-table mb-4">
          <Tabs value={tabIndex} onChange={(e, value) => { setTabIndex(value); setShowErrors(false); }} variant="fullWidth"
                textColor="primary" indicatorColor="primary" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {COMMODITY_TYPES.map((type, i) => (
              <Tab key={type} label={translate(`comexApp.branch.settings.tabs.${type.toLowerCase()}`)}
                   sx={{ fontWeight: tabIndex === i ? 'bold' : 'normal', textTransform: 'none', fontSize: 16 }} />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            <SettingPicker
              bankId={bank?.id}
              commodityType={commodityType}
              selectedSettingId={selectedSettingId}
              onSelectedSettingIdChange={id => setSelectedSettingIdByType({ ...selectedSettingIdByType, [commodityType]: id })}
            />

            {!hasSelectedSetting ? (
              <Box sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {translate('comexApp.bank.rebate.setting.none')}
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', overflowX: 'auto' }}>
                  {SECTIONS.map((section, i) => {
                    const state = i < activeSection ? 'done' : i === activeSection ? 'active' : 'pending';
                    return (
                      <ButtonBase
                        key={section.key}
                        onClick={() => setActiveSection(i)}
                        sx={{
                          flex: '1 0 auto',
                          justifyContent: 'flex-start',
                          gap: 1,
                          pt: 1,
                          pb: 1.5,
                          pr: 2,
                          borderBottom: `2px solid ${i === activeSection ? ACTIVE_COLOR : INACTIVE_COLOR}`,
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: 14,
                          whiteSpace: 'nowrap',
                          fontWeight: state === 'active' ? 500 : 400,
                          color: state === 'pending' ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        <SectionIcon state={state} />
                        {sectionLabel(section)}
                      </ButtonBase>
                    );
                  })}
                </Box>

                <Box sx={{ minHeight: 200, py: 4 }}>
                  <Typography variant="h6" sx={{ mb: 3 }}>{sectionLabel(SECTIONS[activeSection])}</Typography>
                  {sectionKey === 'schedule' ? (
                    <ScheduleSection value={schedule} onChange={setSchedule} showErrors={showErrors} />
                  ) : sectionKey === 'email' ? (
                    <EmailSection value={emails} onChange={setEmails} showErrors={showErrors} />
                  ) : sectionKey === 'letter' ? (
                    <LetterSection
                      enabled={letterEnabled}
                      onEnabledChange={setLetterEnabled}
                      value={letterCategory}
                      onChange={setLetterCategory}
                      showErrors={showErrors}
                      bankName={bank?.name ?? ''}
                      language={letterLanguage}
                      onLanguageChange={setLetterLanguage}
                    />
                  ) : sectionKey === 'reports' ? (
                    <>
                      <ReportSection value={reportSetting} onChange={setReportSetting} showErrors={showErrors} bankName={bank?.name ?? ''} />
                      {reportSetting.enabled && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={handleGenerateReport}
                            disabled={!isReportSettingValid(reportSetting) || generatingReport}
                            startIcon={generatingReport ? <CircularProgress size={16} /> : undefined}
                            sx={{ textTransform: 'none' }}
                          >
                            {translate('comexApp.bank.rebate.report.generate')}
                          </Button>
                        </Box>
                      )}
                      {selectedSettingId && (
                        <PendingAmountChecker
                          rebateSettingId={selectedSettingId}
                          schedule={JSON.stringify({ ...schedule, timeZone: browserTimeZone() })}
                          bankName={bank?.name ?? ''}
                        />
                      )}
                    </>
                  ) : sectionKey === 'document' ? (
                    <DocumentSection value={documentSetting} onChange={setDocumentSetting} showErrors={showErrors} />
                  ) : (
                    <PreviewSection
                      schedule={schedule}
                      emails={emails}
                      letterEnabled={letterEnabled}
                      letterCategory={letterCategory}
                      letterLanguage={letterLanguage}
                      reportSetting={reportSetting}
                      documentSetting={documentSetting}
                      bankName={bank?.name ?? ''}
                      settingName={currentSetting?.name}
                      overpaymentWarning={overpaymentWarning}
                      rebateSettingId={selectedSettingId}
                      onGenerateReport={handleGenerateReport}
                      generatingReport={generatingReport}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button variant="text" disabled={activeSection === 0} onClick={handleBack} sx={{ textTransform: 'none' }}>
                    {translate('entity.action.back')}
                  </Button>
                  {isLastSection ? (
                    <Button variant="contained" color="primary" onClick={handleSave} sx={{ textTransform: 'none' }}>
                      {translate('entity.action.save')}
                    </Button>
                  ) : (
                    <Button variant="contained" color="primary" disabled={!isCurrentSectionValid} onClick={handleNext} sx={{ textTransform: 'none' }}>
                      {translate('entity.action.next')}
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Paper>
      </div>
    </Container>
  );
};

export default BankRebate;
